import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, type Painel, type Pedido, type Representante, type Usuario } from '../api'
import { ordenarDivisoes } from '../cartao'
import { BarraRotacao } from '../componentes/BarraRotacao'
import { Cabecalho } from '../componentes/Cabecalho'
import { FaixaGeral } from '../componentes/FaixaGeral'
import { GradeMarcas } from '../componentes/GradeMarcas'
import { VisaoMarca } from '../componentes/VisaoMarca'
import { Comemoracao } from '../componentes/Comemoracao'
import { ComemoracaoMarca } from '../componentes/ComemoracaoMarca'
import { BarraDaColeta } from '../componentes/BarraDaColeta'
import {
  SEM_MUDANCA,
  compararVendidos,
  emDemonstracao,
  lerParametros,
  nomeDaProximaVisao,
  temposDeRotacao,
} from '../dashboard'
import { usarFluxo } from '../ganchos/usarFluxo'
import { usarRotacao } from '../ganchos/usarRotacao'
import { usarComemoracao } from '../ganchos/usarComemoracao'
import { usarComemoracaoDeMarca } from '../ganchos/usarComemoracaoDeMarca'
import { usarCicloDaColeta } from '../ganchos/usarCicloDaColeta'
import { usarModoProjetor } from '../ganchos/usarModoProjetor'
import { festaDeTeste } from '../marcosMarca'
import {
  comemoracaoDeTeste,
  DURACAO_META_MS,
  DURACAO_METADE_MS,
  DURACAO_MS,
  marcoDe,
} from '../marcos'

/**
 * O telão.
 *
 * O cabeçalho e a faixa do número herói ficam na tela o tempo todo; só a metade
 * de baixo alterna entre a grade das 8 divisões e o detalhe de uma delas.
 *
 * Nada de temporizador nem de listener próprio aqui: o SSE, a reconexão, o
 * relógio da idade e o "acrescentar pedido sem recriar a lista" são do
 * `usarFluxo`; a contagem, o pré-carregamento aos 1:30 e os atalhos de teclado
 * são do `usarRotacao`. Os dois usam UM intervalo cada, para a vida toda — é o
 * que sustenta 4 dias ligado sem vazar.
 */

type Props = {
  usuario: Usuario
  /** `null` no quiosque do telão e para quem não é admin. */
  aoAbrirAdmin: (() => void) | null
  aoSair: () => void
}

export function Dashboard({ usuario, aoAbrirAdmin, aoSair }: Props) {
  const { painel, representantes, conectado, idadeSeg, semearPedidos, semearRanking } = usarFluxo()
  /* MODO PROJETOR (14/10): o telao da sala nao e o notebook de quem acompanha.
     Fica aqui, no topo, porque ele publica `data-projetor` no <html> e vale
     para a tela toda — inclusive para as comemoracoes, que sao `fixed`. */
  const { projetor } = usarModoProjetor()

  /* ---------------------------------------------------------------------
     O BOTÃO "VOLTAR" DO CABEÇALHO (16/10).

     O cabeçalho está aqui e a rotação vive no `PainelVivo`, abaixo — então
     quem sabe se há marca aberta é o filho, e quem desenha o botão é o pai.
     Subir `usarRotacao` para cá resolveria o acesso e quebraria outra coisa: o
     gancho resolve `?visao=` e o primeiro cronômetro na MONTAGEM, e aqui ele
     montaria antes de `GET /api/painel` responder, com a lista de marcas vazia.

     Então o filho PUBLICA, por `aoMudarVisao`. Duas peças, e as duas por um
     motivo: a função de voltar fica numa ref (função em estado é a armadilha
     do `setState`, que trataria a função como updater e guardaria o retorno
     dela), e só o booleano é estado, porque só ele muda o que a tela desenha.
     --------------------------------------------------------------------- */
  const [emMarca, setEmMarca] = useState(false)
  const voltarDaMarca = useRef<(() => void) | null>(null)
  const aoMudarVisao = useCallback((voltar: (() => void) | null) => {
    voltarDaMarca.current = voltar
    setEmMarca(voltar !== null)
  }, [])
  const aoVoltar = useMemo(
    () => (emMarca ? () => voltarDaMarca.current?.() : null),
    [emMarca],
  )

  return (
    /* `data-papel` é o único uso do `usuario` nesta tela: o telão fica dias com
       a janela aberta e ninguém lembra com que sessão ela subiu. Fica no DOM,
       não na tela — o cabeçalho do painel não mostra nome de usuário. */
    <div className="dashboard" data-papel={usuario.papel}>
      <Cabecalho
        coletadoEm={painel?.coletadoEm ?? null}
        idadeSeg={idadeSeg}
        conectado={conectado}
        /* A FAIXA de demonstração saiu (09/10): ela custava uma linha da grade,
           e o que importa no telão é o indicador. O sinal continua existindo,
           como marca discreta aqui — zero altura a mais, e ninguém fotografa
           R$ 105 mi achando que é venda. */
        demonstracao={painel !== null && emDemonstracao(painel)}
        aoVoltar={aoVoltar}
        aoAbrirAdmin={aoAbrirAdmin}
        aoSair={aoSair}
      />

      {painel === null ? (
        /* Carregando é SÓBRIO e sem número: `R$ 0` enquanto a consulta não
           volta é a mentira que o painel não pode contar. */
        <div className="carregando">Carregando o painel…</div>
      ) : (
        <PainelVivo
          projetor={projetor}
          painel={painel}
          representantes={representantes}
          idadeSeg={idadeSeg}
          semearPedidos={semearPedidos}
          semearRanking={semearRanking}
          aoMudarVisao={aoMudarVisao}
        />
      )}
    </div>
  )
}

/* Instância única para a divisão que ainda não tem ranking em memória. `?? []`
   aqui criava um array NOVO a cada segundo (o relógio repinta o Dashboard), e
   as linhas do ranking são `memo`: elas seriam reconciliadas a cada tique
   enquanto o pré-carregamento não voltasse. Não mutar. */
const SEM_RANKING: Representante[] = []

type PropsVivo = {
  projetor: boolean
  painel: Painel
  representantes: Record<string, Representante[]>
  idadeSeg: number
  /* Continua recebido, e o estado de pedidos continua alimentado pelo SSE,
     embora a visão de marca mostre o ranking desde 13/10: o `FeedPedidos` segue
     no código com os seus testes, e repor o feed na tela é trocar um
     componente, não refazer o caminho do dado. */
  semearPedidos: (id: string, pedidos: Pedido[]) => void
  semearRanking: (id: string, representantes: Representante[]) => void
  /**
   * Publica para o `Dashboard` como sair da marca aberta, ou `null` na visão
   * geral. Precisa ser ESTÁVEL (vem de `useCallback` sem dependências lá em
   * cima): o efeito que a chama tem a visão nas dependências, e uma função nova
   * a cada repintura faria o efeito rodar a cada segundo do relógio.
   */
  aoMudarVisao: (voltar: (() => void) | null) => void
}

/**
 * A metade de baixo, mais a rotação.
 *
 * Só monta DEPOIS de o painel existir, e isso é de propósito: `usarRotacao`
 * resolve a visão inicial (`?visao=lez`) e o tempo da primeira contagem UMA
 * vez, na montagem. Montado antes da resposta do `GET /api/painel`, a lista de
 * marcas estaria vazia, `?visao=` seria descartado e a contagem começaria com
 * o tempo de reserva em vez do tempo do cadastro.
 *
 * Depois de montado ele não desmonta mais — a troca de visão acontece aqui
 * dentro, sem criar temporizador nem assinatura nova.
 */
function PainelVivo({
  projetor,
  painel,
  representantes,
  semearPedidos,
  semearRanking,
  aoMudarVisao,
}: PropsVivo) {
  /* Lido uma vez: o endereço não muda sem recarregar a página. */
  const parametros = useMemo(() => lerParametros(location.search), [])
  /* A MESMA ordem da grade. `GradeMarcas` ordena por `ordenarDivisoes`, e é esta
     lista que numera a rotação: com `painel.divisoes` cru, duas divisões que
     empatam na `ordem` saem em ordens diferentes (o servidor desempata por id,
     a grade por nome) e a pílula "a seguir" aponta para outra marca. */
  const marcas = useMemo(() => ordenarDivisoes(painel.divisoes).map(d => d.id), [painel.divisoes])

  const preCarregar = useCallback(
    (id: string) => {
      void api
        .marca(id)
        .then(visao => {
          semearPedidos(id, visao.pedidos)
          /* O ranking vem da MESMA resposta: uma consulta por divisão, não
             duas. Semeado mesmo vazio, para o efeito abaixo não reconsultar. */
          semearRanking(id, visao.representantes ?? [])
        })
        .catch(() => {
          /* Pré-carregamento que falha NÃO derruba a rotação: a visão ainda
             abre, e o efeito abaixo tenta a consulta na hora da troca. */
        })
    },
    [semearPedidos, semearRanking],
  )

  /* Peneirados antes de entrar no gancho: o cadastro guarda os três como
     inteiro sem mínimo, e `segGeral: 0` salvo no admin trocaria a visão a cada
     segundo pelos 4 dias do evento. */
  const tempos = temposDeRotacao(painel.rotacao)

  const rotacao = usarRotacao({
    marcas,
    segGeral: tempos.segGeral,
    segMarca: tempos.segMarca,
    segPrefetch: tempos.segPrefetch,
    preCarregar,
    visaoInicial: parametros.visao,
    rapido: parametros.rapido,
  })

  /* ---------------------------------------------------------------------
     O piscar do card: quais divisões mudaram de valor na última atualização.
     É como alguém de costas para o telão percebe que entrou pedido.

     O valor anterior vive em ref, não em estado: ele não pinta nada por si, e
     guardá-lo em estado faria uma repintura a mais por pedido que entra.
     --------------------------------------------------------------------- */
  const vistos = useRef<Map<string, number | null>>(new Map())
  const [mudaram, setMudaram] = useState<Set<string>>(SEM_MUDANCA)
  const apagarPiscar = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const { mudaram: novas, valores } = compararVendidos(vistos.current, painel.divisoes)
    vistos.current = valores
    if (novas.size === 0) return

    setMudaram(novas)
    /* O conjunto é APAGADO 1 s depois (a duração da animação `.mudou`). Sem
       isso ele só cresce, e no terceiro dia de evento o telão inteiro estaria
       marcado como "acabou de mudar". Um temporizador só, reaproveitado. */
    if (apagarPiscar.current !== null) clearTimeout(apagarPiscar.current)
    apagarPiscar.current = setTimeout(() => setMudaram(SEM_MUDANCA), 1000)
  }, [painel.divisoes])

  useEffect(
    () => () => {
      if (apagarPiscar.current !== null) clearTimeout(apagarPiscar.current)
    },
    [],
  )

  /* A divisão aberta sai do `painel`, não de uma cópia: assim o SSE atualiza o
     número na visão de marca sem passar por consulta nenhuma. Divisão
     descadastrada no meio da volta não encontra nada e a tela volta à grade,
     em vez de ficar em branco. */
  const visao = rotacao.visao
  const divisaoAberta =
    visao === 'geral' ? null : painel.divisoes.find(d => d.id === visao.marca) ?? null

  /* O cabeçalho mora no `Dashboard`, acima deste componente, e é lá que fica o
     botão "Voltar" — então a visão atual sobe por aqui. Trocar de marca
     redispara este efeito, mas o valor publicado é a MESMA função (estável no
     gancho), e o `Dashboard` ignora o valor repetido. */
  const voltarEretomar = rotacao.voltarEretomar
  useEffect(() => {
    aoMudarVisao(visao === 'geral' ? null : voltarEretomar)
  }, [visao, voltarEretomar, aoMudarVisao])

  /* Visão aberta sem pré-carregamento — clique num card, `?visao=` no endereço,
     ou prefetch que falhou: consulta na hora. `semearPedidos` sempre grava a
     chave, inclusive com lista vazia, então isto não vira laço de consulta. */
  const idAberto = divisaoAberta?.id ?? null
  /* O gatilho é o RANKING, não os pedidos: é ele que a visão de marca mostra
     desde 13/10. Com `pedidos` aqui, a divisão cujo feed já estava em memória
     (do SSE) nunca pediria o ranking, e a tela abriria com a lista vazia. */
  const temRanking = idAberto !== null && representantes[idAberto] !== undefined
  useEffect(() => {
    if (idAberto === null || temRanking) return
    preCarregar(idAberto)
  }, [idAberto, temRanking, preCarregar])

  /* COMEMORAÇÃO DE CADA MILHÃO (13/10). O marco sai do total geral, e a rotação
     fica pausada enquanto ela dura — senão a marca que estava na tela troca por
     baixo do confete e a sala volta para outra divisão sem entender por quê.

     `definirPausa` e não `alternarPausa`: se alguém pausou na mão antes do
     milhão cair, a retomada não pode ligar a rotação contra a vontade dele — daí
     o estado de antes ser guardado e devolvido. */
  /* `emDemonstracao` entra nas DUAS comemorações: número fictício não ancora
     nem comemora. Sem isto, a tela aberta antes de o coletor subir gravava a
     meta geral como celebrada em cima de dado inventado, e a festa dos R$ 28 mi
     não aconteceria naquele telão (encontrado em 14/10). */
  const demonstracao = emDemonstracao(painel)
  const emCartaz = usarComemoracao(painel.vendidoTotal, painel.metaGeral, demonstracao)

  /* ENSAIO da comemoração GERAL pelo endereço (`?comemorar=metade|meta|7`).
     A METADE e a META acontecem uma vez cada e ficam gravadas: passado o marco,
     nem limpar a memória as traz de volta, porque o gancho ancora na primeira
     leitura. Este é o caminho para rodar de novo na sala — e a tela SAI sozinha
     quando o tempo acaba, devolvendo o painel sem mexer no endereço. */
  const ensaioGeral = useMemo(() => comemoracaoDeTeste(location.search), [])
  const [ensaioNoAr, setEnsaioNoAr] = useState(ensaioGeral !== null)
  useEffect(() => {
    if (ensaioGeral === null || !ensaioNoAr) return
    const duracao = ensaioGeral.tipo === 'meta'
      ? DURACAO_META_MS
      : ensaioGeral.tipo === 'metade' ? DURACAO_METADE_MS : DURACAO_MS
    const temporizador = setTimeout(() => setEnsaioNoAr(false), duracao)
    return () => clearTimeout(temporizador)
  }, [ensaioGeral, ensaioNoAr])
  const ensaioDaGeral = ensaioGeral !== null && ensaioNoAr

  /* COMEMORAÇÃO POR MARCA (14/10): metade e meta da meta da PRÓPRIA marca, em
     tela cheia com o top 15 de representantes.

     `emCartaz !== null` é o que põe a comemoração geral na frente da fila: a de
     marca espera a do total sair da tela. Duas telas cheias sobrepostas não é
     questão de empilhamento — é a sala vendo confete em cima de confete e não
     lendo nenhum dos dois. */
  const festaDaMarca = usarComemoracaoDeMarca(painel.divisoes, emCartaz !== null, demonstracao)
  /* ENSAIO pelo endereço (`?festa=lez:meta`): a festa real acontece uma vez por
     marca no evento e ninguém pode ensaiá-la. Lido uma vez, como os outros
     parâmetros, e fica na tela enquanto o endereço o tiver — é o que permite
     conferir a tela no telão da sala antes de ela valer. */
  const ensaio = useMemo(() => festaDeTeste(location.search), [])
  const festa = ensaio ?? festaDaMarca
  const divisaoDaFesta =
    festa === null ? null : painel.divisoes.find(d => d.id === festa.marca) ?? null

  /* A contagem até a próxima leitura do ERP, com o ciclo MEDIDO entre os
     dois últimos snapshots — ele não vive no painel, e já mudou duas vezes
     em 13/10 (300 s → 120 s no cofre do coletor). */
  const ciclo = usarCicloDaColeta(painel.coletadoEm)
  const pausadoAntes = useRef<boolean | null>(null)
  const definirPausa = rotacao.definirPausa
  const pausadoAgora = rotacao.pausado
  /* Qualquer tela cheia pausa a rotação, a do total e a de marca: senão a marca
     que estava na tela troca por baixo do confete e a sala volta para outra
     divisão sem entender por quê. */
  const comemorando = emCartaz !== null || festa !== null || ensaioDaGeral
  useEffect(() => {
    if (comemorando) {
      if (pausadoAntes.current === null) pausadoAntes.current = pausadoAgora
      definirPausa(true)
      return
    }
    if (pausadoAntes.current === null) return
    definirPausa(pausadoAntes.current)
    pausadoAntes.current = null
    // `pausadoAgora` fica FORA das dependências de propósito: ele muda quando
    // este efeito pausa, e entrar aqui faria o efeito se reexecutar sozinho.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comemorando, definirPausa])

  /* O ranking da marca em festa, quando ela não é a que está aberta na tela.
     A festa NÃO consulta nada por conta (é a regra da comemoração: nenhuma
     requisição no instante em que ela dispara), mas a marca que bate a meta
     pode nunca ter entrado na rotação — e aí a tela apareceria sem o top 15.
     Reusa o MESMO pré-carregamento da rotação: uma consulta, e o SSE mantém a
     lista viva depois disso. */
  const marcaDaFesta = festa?.marca ?? null
  const festaTemRanking = marcaDaFesta !== null && representantes[marcaDaFesta] !== undefined
  useEffect(() => {
    if (marcaDaFesta === null || festaTemRanking) return
    preCarregar(marcaDaFesta)
  }, [marcaDaFesta, festaTemRanking, preCarregar])

  /* O aviso de fonte degradada SAIU DO TELÃO em 13/10, a pedido: ele acendia a
     cada ciclo. O limite padrão era 180 s e a coleta é de 300 s — entre duas
     coletas a idade passa de 180 por construção, então o telão da sala passava
     ~2 min de cada 5 exibindo "fonte sem resposta" com tudo funcionando.

     A informação não se perdeu: a idade de cada fonte continua na aba Fontes do
     admin, que é a tela de quem opera, e o número velho continua na tela aqui —
     ele nunca zera (SEGURANCA.md). O que saiu foi o alarme no meio da venda. */

  return (
    <>
      <BarraRotacao
        progresso={rotacao.progresso}
        pausado={rotacao.pausado}
        restante={rotacao.restante}
        nomeSeguinte={nomeDaProximaVisao(rotacao.seguinte, painel.divisoes)}
        aoAlternarPausa={rotacao.alternarPausa}
      />

      <FaixaGeral vendidoTotal={painel.vendidoTotal} metaGeral={painel.metaGeral} />

      {divisaoAberta === null ? (
        <GradeMarcas
          divisoes={painel.divisoes}
          aoAbrirMarca={rotacao.abrirMarca}
          mudaram={mudaram}
        />
      ) : (
        <VisaoMarca
          divisao={divisaoAberta}
          representantes={representantes[divisaoAberta.id] ?? SEM_RANKING}
        />
      )}

      {/* Por último no fragmento e `position: fixed` no CSS: ela cobre a tela
          inteira, grade ou visão de marca, sem depender da ordem de empilhamento
          de quem está embaixo. */}
      <BarraDaColeta progresso={ciclo.progresso} intervaloSeg={ciclo.intervaloSeg} />

      {/* A real tem precedência sobre o ensaio: se um milhão cair durante a
          demonstração na sala, o telão mostra o que aconteceu de verdade. */}
      {emCartaz === null && !ensaioDaGeral ? null : (
        <Comemoracao
          projetor={projetor}
          marco={emCartaz?.marco ?? (ensaioGeral?.tipo === 'milhao'
            ? ensaioGeral.marco
            : marcoDe(painel.vendidoTotal))}
          meta={emCartaz?.meta ?? ensaioGeral?.tipo === 'meta'}
          metade={emCartaz?.metade ?? ensaioGeral?.tipo === 'metade'}
          total={painel.vendidoTotal}
          metaGeral={painel.metaGeral}
          divisoes={painel.divisoes}
        />
      )}

      {/* A festa da MARCA. `divisaoDaFesta === null` cobre a marca
          descadastrada no meio da volta: a tela não abre em branco, a festa
          simplesmente não acontece. */}
      {festa === null || divisaoDaFesta === null ? null : (
        <ComemoracaoMarca
          projetor={projetor}
          divisao={divisaoDaFesta}
          tipo={festa.tipo}
          representantes={representantes[divisaoDaFesta.id] ?? SEM_RANKING}
        />
      )}
    </>
  )
}
